<template>
	<sg-modal
		v-model="isOpen"
		:title="title"
		:backdrop-dismiss="false"
		:show-close-button="false"
	>
		<template #header="{ close }">
			<ion-buttons slot="start">
				<ion-button @click="close">
					{{ $t("common.cancel") }}
				</ion-button>
			</ion-buttons>
			<ion-title>{{ title }}</ion-title>
			<ion-buttons slot="end">
				<ion-button @click="handleSave">
					{{ $t("common.save") }}
				</ion-button>
			</ion-buttons>
		</template>

		<base-map
			ref="baseMapRef"
			height="100%"
			:initial-location="initialCenter"
		>
			<template #controls="{ map: mapInstance, isInitialized: mapReady }">
				<!-- Drawing Controls -->
				<div
					v-if="mapInstance && mapReady"
					class="absolute right-4 top-16 z-20 flex flex-col gap-2"
				>
					<ion-button
						v-if="showClear"
						color="danger"
						@click="clearDrawings"
					>
						<ion-icon slot="icon-only" :icon="trashOutline" />
					</ion-button>
				</div>

				<!-- Instructions -->
				<div
					class="absolute bottom-4 left-1/2 z-20 max-w-md -translate-x-1/2 transform rounded-lg bg-white px-4 py-2 text-sm shadow-lg"
				>
					<p class="text-center">
						{{ displayInstructions }}
					</p>
				</div>
			</template>
		</base-map>
	</sg-modal>
</template>

<script setup lang="ts">
	import { IonButtons, IonButton, IonTitle, IonIcon } from "@ionic/vue";
	import { ref, watch, computed, type Ref } from "vue";
	import { trashOutline } from "ionicons/icons";
	import { Polygon, MultiPolygon } from "geojson";
	import { useDrawingLayer } from "@/components/ui/maps/useDrawingLayer";
	import { sendToast } from "@/utils/sendToast";
	import { center, kinks } from "@turf/turf";
	import BaseMap from "@/components/ui/maps/BaseMap.vue";
	import SgModal from "@/components/ui/modals/SgModal.vue";
	import { captureException } from "@sentry/vue";

	interface BoundaryEditModalProps {
		/** Current geometry to edit (undefined for new geometry) */
		geometry?: Polygon | MultiPolygon | null;
		/** Modal title */
		title?: string;
		/** Instructions text */
		instructions?: string;
		/** Center coordinates if no geometry provided */
		defaultCenter?: [number, number];
	}

	const props = withDefaults(defineProps<BoundaryEditModalProps>(), {
		geometry: undefined,
		title: "Edit Boundary",
		instructions: "",
		defaultCenter: undefined,
	});

	const emit = defineEmits<{
		save: [geometry: Polygon | MultiPolygon | null];
	}>();

	const isOpen = ref(false);
	const baseMapRef = ref<InstanceType<typeof BaseMap> | null>(null);

	const displayInstructions = computed(() => {
		if (props.instructions) return props.instructions;
		if (props.geometry) {
			return "Click the boundary to edit it. Drag vertices to adjust. Use the trash button to delete and start over.";
		}
		return "Click to draw points. Double-click to finish the polygon.";
	});

	const initialCenter = computed<[number, number] | undefined>(() => {
		if (props.geometry) {
			const centerFeature = center(props.geometry);
			return centerFeature.geometry.coordinates as [number, number];
		}
		if (props.defaultCenter) {
			return props.defaultCenter;
		}
		return undefined;
	});

	const map = computed(() => baseMapRef.value?.map ?? null);
	const isInitialized = computed(
		() => baseMapRef.value?.isInitialized ?? false,
	);

	// Type assertion needed due to complex Mapbox types
	const {
		completeFeatures,
		showClear,
		clearDrawings,
		changeMode,
		addFeature,
		setEditMode,
		deleteAllFeatures,
		isDrawReady,
	} = useDrawingLayer(map as Ref<mapboxgl.Map | null>, {
		initialMode: "draw_polygon",
	});

	function openModal() {
		isOpen.value = true;
	}

	function closeModal() {
		isOpen.value = false;
	}

	function handleSave() {
		const polygonFeatures = completeFeatures.value.filter(
			(f) =>
				f.geometry.type === "Polygon" ||
				f.geometry.type === "MultiPolygon",
		);

		if (polygonFeatures.length === 0) {
			if (props.geometry) {
				// Allow removing existing boundaries by saving null
				emit("save", null);
				closeModal();
				return;
			}
			// Require drawing when creating a new boundary
			sendToast("Please draw a boundary", 2000, "warning");
			return;
		}

		const geometry = polygonFeatures[0].geometry as Polygon | MultiPolygon;

		// Check for self-intersections
		try {
			const kinksResult = kinks(geometry);
			if (kinksResult.features.length > 0) {
				sendToast(
					"Boundary is invalid: the polygon lines cross themselves. Please redraw without intersections.",
					4000,
					"danger",
				);
				return;
			}
		} catch (error) {
			// If kinks fails for any reason, show a generic validation error
			captureException(error);
			sendToast(
				"Boundary is invalid. Please check the shape and try again.",
				3000,
				"danger",
			);
			return;
		}

		emit("save", geometry);
		closeModal();
	}

	function setupMapLayers() {
		if (!map.value) return;

		deleteAllFeatures();

		if (props.geometry) {
			const feature = {
				type: "Feature" as const,
				geometry: props.geometry,
				properties: {},
			};
			addFeature(feature);
			setEditMode();
		} else {
			changeMode("draw_polygon");
		}
	}

	watch(
		[isOpen, isInitialized, isDrawReady],
		([open, mapReady, drawReady]) => {
			if (open && mapReady && drawReady) {
				setupMapLayers();
			}
		},
	);

	defineExpose({ openModal, closeModal });
</script>
